import { z } from 'zod';

export const ENABLE_DEBUG_PERFORMANCE_TRACKING_STORAGE_KEY =
  'enable_debug_performance_tracking_storage_key';

export enum FeatureName {
  AUTHENTICATION = 'AUTHENTICATION',
  LANGUAGE_SUGGESTIONS = 'LANGUAGE_SUGGESTIONS',
  CONSISTENCY_CHECK = 'CONSISTENCY_CHECK',
  WRITE = 'WRITE',
  REWRITE = 'REWRITE',
  RESEARCH = 'RESEARCH',
  NOTIFICATIONS = 'NOTIFICATIONS',
  CHECKS = 'CHECKS',
  CHATPDF = 'CHAT_PDF',
  AI_CHAT = 'AI_CHAT',
  COMING_SOON = 'COMING_SOON',
  TEMPLATES = 'TEMPLATES',
  TIPTAP = 'TIPTAP',
  APP = 'APP',
}

export enum SubFeature {
  AUTH_ONE_LOGIN_TOKEN = 'AUTH_ONE_LOGIN_TOKEN',
  AUTH_PP_USER = 'AUTH_PP_USER',
  // WRITE
  KEEP_WRITING = 'KEEP_WRITING',
  WRITE = 'WRITE',
  //REWRITE
  PARAPHRASE = 'PARAPHRASE',
  TRIM = 'TRIM',
  ACADEMIC_TONE = 'ACADEMIC_TONE',
  SYNONYMS = 'SYNONYMS',
  SYNONYMS_DEFINATIONS = 'SYNONYMS_DEFINATIONS',
  TRANSLATE = 'TRANSLATE',

  // Research
  ASK_QUESTION = 'ASK_QUESTION',
  SEARCH = 'SEARCH',
  LIBRARY = 'LIBRARY',
  HISTORY = 'HISTORY',
  CITATION_STYLE = 'CITATION_STYLE',

  //Chat-pdf
  PDF_METADATA = 'PDF_METADATA',
  SUMMARY_METADATA = 'SUMMARY_METADATA',
  SUMMARY = 'SUMMARY',
  RELATED_PAPERS = 'RELATED_PAPERS',
  CHAT = 'CHAT',
  UPLOAD_DOCUMENT = 'UPLOAD_DOCUMENT',
}

export enum OperationType {
  API_CALL = 'API_CALL',
  COMPONENT_LOAD = 'COMPONENT_LOAD',
  DOCUMENT_READ = 'DOCUMENT_READ',
  GET_PARAGRAPHS = 'GET_PARAGRAPHS',
  FIRST_SUGGESTIONS_CHUNK_LOAD = 'FIRST_SUGGESTIONS_CHUNK_LOAD',

  // User Actions
  ACCEPT_SUGGESTION = 'ACCEPT_SUGGESTION',
  REJECT_SUGGESTION = 'REJECT_SUGGESTION',
  SELECT_SENTENCE = 'SELECT_SENTENCE',
  APPLY_PARAPHRASE = 'APPLY_PARAPHRASE',
  APPLY_TRANSLATION = 'APPLY_TRANSLATION',
  CONNECTION = 'CONNECTION',
  LOADING_TIME = 'LOADING_TIME',
  APP_LOADING_TIME = 'APP_LOADING_TIME',
  REPLACE_SELECTION = 'REPLACE_SELECTION',
  UPLOADING_TIME = 'UPLOAD_TIME',
  FIRST_SUGGESTION_VISIBLE = 'FIRST_SUGGESTION_VISIBLE',
  APPLY_REWRITE = 'APPLY_REWRITE',
  MOVE_CURSOR = 'MOVE_CURSOR',
  APPLY_CONSISTENCY = 'APPLY_CONSISTENCY',
  GET_SELECTION = 'GET_SELECTION',
  ON_UPDATED = 'ON_UPDATED',
  SENTENCES_SEGMENTATION = 'SENTENCES_SEGMENTATION',

  // Agents. Enum member names keep the AI_CHAT_ prefix (greppable), but the
  // string VALUES omit it: the perf marker is `featureName_operationType`, so
  // with featureName=AI_CHAT a prefixed value would produce a doubled
  // `ai_chat_ai_chat_…` marker. Dropping it yields clean `ai_chat_<op>` markers.
  // core latency (sampled durations)
  AI_CHAT_TIME_TO_FIRST_TOKEN = 'TIME_TO_FIRST_TOKEN', // submit → first token (A1)
  AI_CHAT_REQUEST_ACK = 'REQUEST_ACK', // submit → run_started (A2)
  AI_CHAT_MODEL_TTFT = 'MODEL_TTFT', // run_started → first token (A3)
  AI_CHAT_RESPONSE_TOTAL = 'RESPONSE_TOTAL', // submit → run_completed (A4)
  // streaming UX (sampled durations)
  AI_CHAT_CONTENT_STREAM = 'CONTENT_STREAM', // first token → content done (B1/B2)
  AI_CHAT_FINALISING = 'FINALISING', // content done → run_completed (B3)
  AI_CHAT_TOOL_CALL = 'TOOL_CALL', // tool_call_started → completed (B4)
  AI_CHAT_HITL_WAIT = 'HITL_WAIT', // web-search prompt → user decision (B5)
  // context preparation (sampled durations)
  AI_CHAT_CONTEXT_PREP = 'CONTEXT_PREP', // submit → POST dispatched (C1)
  AI_CHAT_EXTERNAL_DOC_READ = 'EXTERNAL_DOC_READ', // run_paused(doc) → continue (C2)
  AI_CHAT_ATTACH_TOTAL = 'ATTACH_TOTAL', // first S3 done → all files ready (C3)
  AI_CHAT_ATTACH_REGISTER = 'ATTACH_REGISTER', // flushBatch → addResources resolved (C4)
  AI_CHAT_ATTACH_EXTRACTION = 'ATTACH_EXTRACTION', // SSE start → all extracted (C4)
  // error / reliability (unsampled outcome counters)
  AI_CHAT_RUN_OUTCOME = 'RUN_OUTCOME', // one per run: complete|error|quota|cancelled|empty (D1/D2)
  AI_CHAT_UPLOAD_OUTCOME = 'UPLOAD_OUTCOME', // one per file: completed|failed + reason (D4)
  AI_CHAT_SSE_RECONNECT = 'SSE_RECONNECT', // extraction SSE reconnect attempt (D5)
  AI_CHAT_SSE_TIMEOUT = 'SSE_TIMEOUT', // extraction SSE 15-min timeout fired (D5)
  AI_CHAT_SESSION_OUTCOME = 'SESSION_OUTCOME', // session lifecycle failures: start/history/messages (D6)

  // Authentication (login / signup) — featureName=AUTHENTICATION, so values omit
  // the feature word to avoid a doubled marker. Markers: perf_authentication_<value>.
  AUTH_FLOW_TOTAL = 'FLOW_TOTAL', // login start (post-redirect) → destination page ready
  AUTH_TOKEN_FETCH = 'TOKEN_FETCH', // /oauth/token round-trip
  AUTH_USER_FETCH = 'USER_FETCH', // /me round-trip
  AUTH_FIRST_DOC_CREATE = 'FIRST_DOC_CREATE', // new-user createDraft (signup only)
}

export const MeasurementDetailSchema = z.record(z.string(), z.unknown());

export type MeasurementDetail = z.infer<typeof MeasurementDetailSchema>;

export interface PerformanceTrackerKey {
  featureName: FeatureName;
  subFeature?: SubFeature;
  operationType: OperationType;
}

export interface PerformanceTrackerData extends PerformanceTrackerKey {
  detail?: MeasurementDetail;
}
